import {useEffect, useRef, useState} from "react";
import {isEqual} from "lodash";
import {ResumableEventSource} from "../llm/ResumableEventSource";

// Connection timeout in milliseconds - fail after this long with no data
const SSE_CONNECTION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
// Auto-reconnect after a transport error when resume is enabled.
//
// CloudFront returns a BURST of 503s when re-establishing an SSE connection to
// an existing channel (it holds the original origin connection; reconnect races
// it). Each 503 fails fast, so a small fixed-delay budget (old: 5 x 1s) burns
// out in ~5s — and when it does, the error event is tagged willRetry=false and
// LLMClient escalates to a fatal "AI service temporarily unavailable" message
// even though the backend agent is alive and resumable. Use a larger budget
// with capped exponential backoff so reconnect rides through the 503 burst.
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 5000;
const MAX_AUTO_RECONNECTS = 10;

// Capped exponential backoff for reconnect attempt N (1-based):
// 1s, 2s, 4s, 5s, 5s, ... — total window ≈ 40s over 10 attempts, which
// comfortably outlasts an observed CloudFront SSE-reconnect 503 burst.
export const reconnectBackoffMs = (attemptNumber) =>
  Math.min(RECONNECT_BASE_MS * Math.pow(2, Math.max(0, attemptNumber - 1)), RECONNECT_MAX_MS);

// If still CONNECTING after this long, the HTTP response never arrived — fail fast
const SSE_CONNECTING_TIMEOUT_MS = 30 * 1000; // 30s (server sends keepalive at 25s)

// NOTE: this only works where the SSE event type has not been set; for that you need to addEventListener for each type
export const useSSEListener = ({
  url,
  onMessage = null,
  onError = null,
  onOpen = null,
  eventTypes = [],
  timeoutMs = SSE_CONNECTION_TIMEOUT_MS,
  // step 11+12 wiring: when set, use ResumableEventSource (which sends
  // Last-Event-ID on (re)connect) and auto-reconnect on transient errors
  // up to MAX_AUTO_RECONNECTS times.
  lastEventId = "",
  autoReconnect = false,
  // PLEX-4218: extra request headers (e.g. Authorization) for the
  // ResumableEventSource fetch — native EventSource can't send custom
  // headers at all, so this only applies when autoReconnect is on.
  headers = undefined,
  // PLEX-6261: with autoReconnect, the URL to connect on instead when `url` answers 404
  // (a backend deployed before the stream's /sse path). See ResumableEventSource.
  fallbackUrl = undefined,
}) => {
  const mhRef = useRef(onMessage);
  const ehRef = useRef(onError);
  const ohRef = useRef(onOpen);
  const [eventSource, setEventSource] = useState(null);

  useEffect(() => {
    mhRef.current = onMessage;
    ehRef.current = onError;
    ohRef.current = onOpen;
  }, [onMessage, onError, onOpen]);

  useEffect(() => {
    if (url) {
      const seenMessages = new Set();
      const connectionStartTime = Date.now();
      let lastDataReceivedTime = Date.now();
      let timeoutCheckInterval = null;

      const handleMessage = (e, ref) => {
        if (!e.lastEventId || !seenMessages.has(e.lastEventId)) {
          if (e.lastEventId) {
            seenMessages.add(e.lastEventId);
          }
          // Update last data received time on any message
          lastDataReceivedTime = Date.now();
          ref.current && ref.current(e);
        }
      };
      // When autoReconnect is on, use ResumableEventSource (sends
      // Last-Event-ID on reconnect). Otherwise keep the native EventSource
      // so existing call sites that haven't opted in are unchanged.
      const Impl = autoReconnect ? ResumableEventSource : EventSource;
      const opts = autoReconnect ? { lastEventId, headers, fallbackUrl } : undefined;
      const eventSource = new Impl(url, opts);
      let reconnectsRemaining = MAX_AUTO_RECONNECTS;
      setEventSource(eventSource);
      eventSource.addEventListener("open", e => handleMessage(e, ohRef));
      eventSource.addEventListener("message", e => handleMessage(e, mhRef));
      eventSource.addEventListener("error", e => {
        // Add connection state information to help distinguish between different error types
        const stateNames = ['CONNECTING', 'OPEN', 'CLOSED'];
        const readyState = eventSource.readyState;
        const connectionDuration = Date.now() - connectionStartTime;

        // Enrich error event with detailed context for downstream handlers
        e.connectionState = stateNames[readyState] || 'UNKNOWN';
        e.readyStateValue = readyState;
        e.url = url;
        e.connectionDuration = connectionDuration;
        e.eventSourceReadyState = readyState;

        // Tag the event with whether the SSE layer will retry. Downstream
        // consumers (LLMClient.handleStreamEvent) use this to know if they
        // should treat the error as fatal or let the auto-reconnect path
        // resolve it. Without this gate, a transient SSE drop would null
        // streamId and cancel the turn (DELETE /api/stream/<id>) before the 1s reconnect
        // setTimeout fires — defeating the entire ResumableEventSource +
        // Last-Event-ID + backend-grace architecture.
        const willRetry = autoReconnect
            && typeof eventSource.reconnect === "function"
            && readyState !== ResumableEventSource.CLOSED
            && reconnectsRemaining > 0;
        e.willRetry = willRetry;
        e.reconnectsRemaining = reconnectsRemaining;

        // Only log to console.error (and thus Sentry) if there's NO error handler
        // If there is a handler, let downstream code decide whether this error is Sentry-worthy
        if (!ehRef.current) {
          console.error(`SSE connection error: readyState=${stateNames[readyState] || readyState} (${readyState})`, {
            url,
            connectionDuration,
            readyState,
            eventType: e.type,
            isTrusted: e.isTrusted
          });
        }

        handleMessage(e, ehRef);

        // step 11+12: auto-reconnect with Last-Event-ID up to a small
        // retry budget. Skips reconnect if the consumer closed us, if
        // we've used all retries, or if the URL changed (the outer
        // useEffect will rebuild from scratch).
        if (willRetry) {
          reconnectsRemaining -= 1;
          const attemptNumber = MAX_AUTO_RECONNECTS - reconnectsRemaining; // 1 on first retry
          const delay = reconnectBackoffMs(attemptNumber);
          setTimeout(() => {
            try {
              eventSource.reconnect();
            } catch (err) {
              console.warn("[useSSEListener] reconnect failed", err?.message || err);
            }
          }, delay);
          return;
        }
        if (readyState !== EventSource.CLOSED) {
          eventSource.close();
        }
      });
      eventTypes.forEach(eType => {
        eventSource.addEventListener(eType, e => handleMessage(e, mhRef));
      });

      // Set up timeout monitoring - check every 30 seconds if we've received data recently
      // This defends against stalled connections where the server/Redis fails but doesn't close the connection
      if (timeoutMs > 0) {
        timeoutCheckInterval = setInterval(() => {
          const timeSinceLastData = Date.now() - lastDataReceivedTime;
          const readyState = eventSource.readyState;

          // If still CONNECTING with no data past the first keepalive window, fail fast
          const effectiveTimeout = readyState === EventSource.CONNECTING
            ? Math.min(timeoutMs, SSE_CONNECTING_TIMEOUT_MS)
            : timeoutMs;

          // If we haven't received any data in timeoutMs, treat as timeout
          if (timeSinceLastData > effectiveTimeout) {
            console.warn(`SSE connection timeout after ${Math.round(timeSinceLastData / 1000)}s with no data (readyState=${readyState})`);

            // Create synthetic timeout error event
            const timeoutError = new Event('error');
            const stateNames = ['CONNECTING', 'OPEN', 'CLOSED'];
            timeoutError.connectionState = stateNames[readyState] || 'UNKNOWN';
            timeoutError.readyStateValue = readyState;
            timeoutError.url = url;
            timeoutError.connectionDuration = Date.now() - connectionStartTime;
            timeoutError.eventSourceReadyState = readyState;
            timeoutError.isTimeout = true;
            timeoutError.timeSinceLastData = timeSinceLastData;

            // Close the connection
            eventSource.close();

            // Notify error handler if present
            if (ehRef.current) {
              ehRef.current(timeoutError);
            }

            // Clear the interval since we're done
            clearInterval(timeoutCheckInterval);
          }
        }, 30000); // Check every 30 seconds
      }

      return () => {
        if (timeoutCheckInterval) {
          clearInterval(timeoutCheckInterval);
        }
        eventSource.close();
        setEventSource(null);
      };
    } else {
      setEventSource(null);
    }
  }, [url, JSON.stringify(eventTypes.sort()), timeoutMs]);

  return eventSource;
}
